import { sendMessage, setNamespace } from "webext-bridge/window";

import {
  BRIDGE_NAMESPACE,
  type ExtensionMessageTypeValue,
} from "@punto/frontend-shared/extension";

setNamespace(BRIDGE_NAMESPACE);

// webext-bridge accepts JsonValue, but callers may pass objects with optional fields (string | undefined).
// Using Record<string, unknown> as a practical bridge type.
type BridgeData = Record<string, unknown> | undefined;

/**
 * Fire-and-forget: send a message to the extension background via webext-bridge.
 * Used for LOGOUT where we don't need the response.
 */
export function sendBridgeMessage(
  messageId: ExtensionMessageTypeValue,
  data: BridgeData,
): void {
  void sendMessage(messageId, data as never, "background").catch(
    (err: unknown) => {
      console.warn("[Extension Bridge] Message failed:", messageId, err);
    },
  );
}

/**
 * Send a message to the extension background and wait for a response.
 * Returns null if the extension is not installed (no content script relay → timeout).
 */
export async function sendBridgeMessageSafe(
  messageId: ExtensionMessageTypeValue,
  data: BridgeData,
  timeoutMs = 2000,
): Promise<Record<string, unknown> | null> {
  try {
    const result = await Promise.race([
      sendMessage(messageId, data as never, "background"),
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), timeoutMs),
      ),
    ]);
    return result as Record<string, unknown> | null;
  } catch (err) {
    console.warn("[Extension Bridge] Message failed:", messageId, err);
    return null;
  }
}
